/**
 * Rich User Profile Types
 *
 * Captures structured professional and demographic attributes that enable
 * segment-based analysis in reports. For example:
 *   "What do Directors say vs ICs?"
 *   "How do PMs vs Engineers rank this initiative?"
 *   "What do people with 10+ years of experience think?"
 */

// ── Vocabulary types ──────────────────────────────────────────────────────────

export type SeniorityLevel =
  | 'intern'
  | 'junior'
  | 'mid'
  | 'senior'
  | 'staff'
  | 'principal'
  | 'lead'
  | 'manager'
  | 'sr_manager'
  | 'director'
  | 'sr_director'
  | 'vp'
  | 'svp'
  | 'evp'
  | 'c_suite';

export type JobFamily =
  | 'Engineering'
  | 'Product'
  | 'Design'
  | 'Data & Analytics'
  | 'Research'
  | 'Marketing'
  | 'Sales'
  | 'Operations'
  | 'Finance'
  | 'Legal'
  | 'HR & People'
  | 'Customer Success'
  | 'Other';

export type ProfileVisibility = 'private' | 'workspace' | 'public';

// ── Core profile ──────────────────────────────────────────────────────────────

export interface UserRichProfile {
  userId: string;

  // Role & Title
  jobTitle?: string;
  jobFamily?: JobFamily;
  /** Normalised seniority band (cross-company comparable) */
  seniorityLevel?: SeniorityLevel;
  /** Company-specific level code, e.g. "L5", "IC6", "M3" */
  orgLevel?: string;

  // Experience
  yearsInCurrentRole?: number;
  yearsAtCompany?: number;
  yearsTotalExperience?: number;

  // Domain & Product Expertise
  expertiseAreas: string[];
  productAreas: string[];

  // Org Context
  department?: string;
  teamName?: string;

  // Location
  location?: string;
  timezone?: string;

  // Self-description
  bio?: string;

  /** Public URL of profile photo (Supabase Storage), if set */
  avatarUrl?: string;

  /** Job role slugs pinned for home dashboard defaults (Build Contract §1D) */
  featuredJobRoleSlugs: string[];

  // Flexible org-specific segmentation attributes
  customAttributes: Record<string, string | number | boolean>;

  // Visibility / consent
  profileVisibility: ProfileVisibility;

  createdAt: Date;
  updatedAt: Date;
}

// ── Segment shape (lightweight, used by analysis / reporting) ─────────────────

export interface UserProfileSegment {
  userId: string;
  /** Display name loaded separately from public.users */
  displayName?: string;
  jobFamily?: JobFamily;
  seniorityLevel?: SeniorityLevel;
  /** Raw company-level code */
  orgLevel?: string;
  yearsTotalExperience?: number;
  expertiseAreas: string[];
  productAreas: string[];
  department?: string;
  teamName?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export const SENIORITY_LEVELS: { value: SeniorityLevel; label: string }[] = [
  { value: 'intern',      label: 'Intern / Apprentice' },
  { value: 'junior',      label: 'Junior / Associate' },
  { value: 'mid',         label: 'Mid-level' },
  { value: 'senior',      label: 'Senior' },
  { value: 'staff',       label: 'Staff' },
  { value: 'principal',   label: 'Principal' },
  { value: 'lead',        label: 'Lead' },
  { value: 'manager',     label: 'Manager' },
  { value: 'sr_manager',  label: 'Senior Manager' },
  { value: 'director',    label: 'Director' },
  { value: 'sr_director', label: 'Senior Director' },
  { value: 'vp',          label: 'VP' },
  { value: 'svp',         label: 'SVP' },
  { value: 'evp',         label: 'EVP' },
  { value: 'c_suite',     label: 'C-Suite / Executive' },
];

export const JOB_FAMILIES: JobFamily[] = [
  'Engineering',
  'Product',
  'Design',
  'Data & Analytics',
  'Research',
  'Marketing',
  'Sales',
  'Operations',
  'Finance',
  'Legal',
  'HR & People',
  'Customer Success',
  'Other',
];

export const PROFILE_VISIBILITY_LABELS: Record<ProfileVisibility, string> = {
  private:   'Private – only I can see this',
  workspace: 'Workspace – visible to workspace members',
  public:    'Public – visible to all users',
};

/** Convert a DB snake_case row to a camelCase UserRichProfile */
export function rowToUserRichProfile(row: Record<string, unknown>): UserRichProfile {
  return {
    userId:               row.user_id as string,
    jobTitle:             (row.job_title as string | null) ?? undefined,
    jobFamily:            (row.job_family as JobFamily | null) ?? undefined,
    seniorityLevel:       (row.seniority_level as SeniorityLevel | null) ?? undefined,
    orgLevel:             (row.org_level as string | null) ?? undefined,
    yearsInCurrentRole:   (row.years_in_current_role as number | null) ?? undefined,
    yearsAtCompany:       (row.years_at_company as number | null) ?? undefined,
    yearsTotalExperience: (row.years_total_experience as number | null) ?? undefined,
    expertiseAreas:       (row.expertise_areas as string[] | null) ?? [],
    productAreas:         (row.product_areas as string[] | null) ?? [],
    department:           (row.department as string | null) ?? undefined,
    teamName:             (row.team_name as string | null) ?? undefined,
    location:             (row.location as string | null) ?? undefined,
    timezone:             (row.timezone as string | null) ?? undefined,
    bio:                  (row.bio as string | null) ?? undefined,
    avatarUrl:            (row.avatar_url as string | null) ?? undefined,
    featuredJobRoleSlugs:   (row.featured_job_role_slugs as string[] | null) ?? [],
    customAttributes:     (row.custom_attributes as Record<string, string | number | boolean> | null) ?? {},
    profileVisibility:    (row.profile_visibility as ProfileVisibility) ?? 'workspace',
    createdAt:            new Date(row.created_at as string),
    updatedAt:            new Date(row.updated_at as string),
  };
}

/** Convert a camelCase UserRichProfile to a DB snake_case upsert payload */
export function userRichProfileToRow(profile: Partial<UserRichProfile> & { userId: string }): Record<string, unknown> {
  const row: Record<string, unknown> = {
    user_id:    profile.userId,
    updated_at: new Date().toISOString(),
  };

  if (profile.jobTitle             !== undefined) row.job_title              = profile.jobTitle || null;
  if (profile.jobFamily            !== undefined) row.job_family             = profile.jobFamily || null;
  if (profile.seniorityLevel       !== undefined) row.seniority_level        = profile.seniorityLevel || null;
  if (profile.orgLevel             !== undefined) row.org_level              = profile.orgLevel || null;
  if (profile.yearsInCurrentRole   !== undefined) row.years_in_current_role  = profile.yearsInCurrentRole ?? null;
  if (profile.yearsAtCompany       !== undefined) row.years_at_company       = profile.yearsAtCompany ?? null;
  if (profile.yearsTotalExperience !== undefined) row.years_total_experience = profile.yearsTotalExperience ?? null;
  if (profile.expertiseAreas       !== undefined) row.expertise_areas        = profile.expertiseAreas;
  if (profile.productAreas         !== undefined) row.product_areas          = profile.productAreas;
  if (profile.department           !== undefined) row.department             = profile.department || null;
  if (profile.teamName             !== undefined) row.team_name              = profile.teamName || null;
  if (profile.location             !== undefined) row.location               = profile.location || null;
  if (profile.timezone             !== undefined) row.timezone               = profile.timezone || null;
  if (profile.bio                  !== undefined) row.bio                    = profile.bio || null;
  if ('avatarUrl' in profile) {
    row.avatar_url =
      profile.avatarUrl && String(profile.avatarUrl).trim().length > 0 ? String(profile.avatarUrl).trim() : null;
  }
  if (profile.featuredJobRoleSlugs !== undefined) row.featured_job_role_slugs = profile.featuredJobRoleSlugs;
  if (profile.customAttributes     !== undefined) row.custom_attributes      = profile.customAttributes;
  if (profile.profileVisibility    !== undefined) row.profile_visibility     = profile.profileVisibility;

  return row;
}
